/* @flow */
import cx from 'classnames';
import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { capitalize } from 'lodash';

import Row from 'components/Row';
import { handleInputChange, handlePaste } from 'reducers/directAddForm';
import UsersParticipantsTableInputRow from './UsersParticipantsTableInputRow';

import type { UserRow } from 'reducers/directAddForm';
import './UsersParticipantsTableInput.scss';

type Props = {
  className?: string,
  userType: string,
  minimumRows?: number,
  scroll?: boolean,
  supportSms: boolean,
  maxRows: number,
  alert: typeof React.Fragment,

  // Redux
  userRows: Array<UserRow>,
  handleInputChange: (index: number, role: string, userRow: UserRow) => void,
  handlePaste: (startIndex: number, role: string, rows: string[][], emailRequired: boolean) => void,
};

type State = {
  shouldScroll: boolean,
};

class UsersParticipantsTableInput extends React.Component<Props, State> {
  static defaultProps = {
    minimumRows: 5,
    scroll: true,
  };

  state: State = {
    shouldScroll: false,
  };

  componentDidUpdate() {
    if (this.state.shouldScroll) {
      const { tableContainer } = this.refs;
      tableContainer.scrollTop = tableContainer.scrollHeight;
    }
  }

  handleChange = (number: number, updatedRow: UserRow) => {
    const { userType } = this.props;
    const index = number - 1;

    this.props.handleInputChange(index, userType, updatedRow);
  };

  addPastedRowsToStore = (number: number, rows: string[][], emailRequired: boolean) => {
    const { userType } = this.props;
    const index = number - 1;
    this.props.handlePaste(index, userType, rows, emailRequired);
  };

  render() {
    const { className, userType, scroll, supportSms, userRows, maxRows, alert } = this.props;

    const nameLabel = `${capitalize(userType)} name`;
    const emailRequired = userType === 'teacher';
    const showAlert = userRows.length >= maxRows;

    return (
      <div className={cx('UsersParticipantsTableInput', className)}>
        <header className="UsersParticipantsTableInput-header">
          <span className="UsersParticipantsTableInput-cell--number UsersParticipantsTableInput-cell" />
          <div className="UsersParticipantsTableInput-cell">{nameLabel}</div>
          <div className="UsersParticipantsTableInput-cell">
            {emailRequired || !supportSms ? 'Email' : 'Phone or email'}
          </div>
          {supportSms && (
            <div className="UsersParticipantsTableInput-cell">
              {emailRequired ? 'Phone' : 'Secondary phone or email'}
            </div>
          )}
        </header>
        <Row flush={!showAlert} size="small">
          <div
            className={cx({ 'UsersParticipantsTableInput-container': scroll })}
            ref="tableContainer">
            <div className="UsersParticipantsTableInput-body">
              {userRows.map((user, index) => (
                <UsersParticipantsTableInputRow
                  autofocus={index === 0}
                  key={`row-${index}`}
                  namePlaceholder={nameLabel}
                  number={++index}
                  supportSms={supportSms}
                  userRow={user}
                  onChange={this.handleChange}
                  emailRequired={emailRequired}
                  onPaste={this.addPastedRowsToStore}
                />
              ))}
            </div>
          </div>
        </Row>
        {showAlert && (
          <Row flush={!showAlert} data-test="user-participants-table-input-alert">
            {alert}
          </Row>
        )}
      </div>
    );
  }
}

function mapStateToProps(state, ownProps) {
  return {
    userRows: state.directAddForm[`${ownProps.userType}s`],
    maxRows: state.directAddForm.maxRows,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return bindActionCreators({ handleInputChange, handlePaste }, dispatch);
}

export { UsersParticipantsTableInput };

export default connect(mapStateToProps, mapDispatchToProps)(UsersParticipantsTableInput);
